(() => {
  const track = [0, 1, 2, 3, 4, 5, 6];
  const positionClasses = track.map((position) => `is-position-${position}`);
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const LOSE_STEPS = [1, 2, 4];
  // priceName from openapi_v2 → board steps (legacy act_price_id: 1→317, 2→318, 3→319, 4→320)
  const PRIZE_STEPS = {
    1034: 6, // 1st (was 317)
    1035: 5, // 2nd (was 318)
    1036: 3, // 3rd (was 319)
    1037: null, // no prize → random from LOSE_STEPS (was 320)
  };

  const DICE_ANIMATION_MS = 2000;
  const ACT_ID = "141";
  const MSG_ENTER_EMAIL = "Please enter your email first.";
  const MSG_EMAIL_LIMIT = "Email exceeded the limit for participation.";
  const MSG_IP_LIMIT = "The number of requests from the same IP address exceeded the limit.";
  const MSG_SERVER_ERROR = "Something went wrong. Please try again.";
  const COPY_BUBBLE_MS = 2000;

  const instances = new Set();

  const extractDiscountCode = (response) => {
    // Discount lives on response.data.code — do not use top-level `code` (API status).
    const code = response?.data?.code;
    if (code == null) return null;
    const trimmed = String(code).trim();
    return trimmed || null;
  };

  const getKlaviyoFormRoot = (root) =>
    root?.querySelector?.('[class^="klaviyo-form-"]') || null;

  const getKlaviyoFormId = (lottery, form) => {
    const fromData = lottery?.getAttribute?.("data-klaviyo-form-id");
    if (fromData) return fromData;

    const className = [...(form?.classList || [])].find((name) =>
      name.startsWith("klaviyo-form-")
    );
    return className ? className.slice("klaviyo-form-".length) : "";
  };

  const wait = (duration) =>
    new Promise((resolve) => {
      window.setTimeout(resolve, duration);
    });

  const pickLoseSteps = () => {
    if (!window.crypto?.getRandomValues) {
      return LOSE_STEPS[Math.floor(Math.random() * LOSE_STEPS.length)];
    }

    const values = new Uint32Array(1);
    window.crypto.getRandomValues(values);
    return LOSE_STEPS[values[0] % LOSE_STEPS.length];
  };

  const getStepsForPriceName = (priceName) => {
    const id = Number(priceName);
    if (id === 1037) return pickLoseSteps();
    return PRIZE_STEPS[id] || null;
  };

  const getErrorMessage = (actStatus) => {
    if (actStatus === "no_finish") return MSG_EMAIL_LIMIT;
    if (actStatus === "no_finish_ip") return MSG_IP_LIMIT;
    return MSG_SERVER_ERROR;
  };

  const ajaxLottery = (email) => {
    return fetch("https://global.shokz.com/lottery/openapi_v2", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        mbId: email,
        actId: ACT_ID,
        usageScenarios: "cs",
      }),
    }).then((res) => {
      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      return res.json();
    });
  };

  const formatStatus = (template, steps) =>
    String(template || "").replaceAll("{steps}", String(steps));

  const getDiceTemplate = (lottery, steps) =>
    lottery.querySelector(`template[data-utmb26-lottery-dice="${steps}"]`);

  const showDiceForSteps = (lottery, slot, steps) => {
    if (!slot) return;

    const template = getDiceTemplate(lottery, steps);
    if (!template) return;

    const fragment = template.content.cloneNode(true);
    slot.replaceChildren(fragment);
  };

  const parseModalConfig = (lottery) => {
    const node = lottery.querySelector("[data-utmb26-lottery-modal-config]");
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  };

  const setupModal = (lottery) => {
    const modal = lottery.querySelector("[data-utmb26-lottery-modal]");
    const dialog = lottery.querySelector("[data-utmb26-lottery-modal-dialog]");
    const closeButtons = lottery.querySelectorAll("[data-utmb26-lottery-modal-close]");
    const titleEl = lottery.querySelector("[data-utmb26-lottery-modal-title]");
    const subtitleEl = lottery.querySelector("[data-utmb26-lottery-modal-subtitle]");
    const heartEl = lottery.querySelector("[data-utmb26-lottery-modal-heart]");
    const imageEl = lottery.querySelector("[data-utmb26-lottery-modal-image]");
    const config = parseModalConfig(lottery);

    if (!modal || !dialog || !titleEl || !subtitleEl || !config) {
      return { open: () => {} };
    }

    let lastFocus = null;

    const close = () => {
      modal.classList.remove("is-open");
      modal.hidden = true;
      modal.setAttribute("aria-hidden", "true");
      document.body.classList.remove("utmb26-lottery-modal-open");
      if (lastFocus && typeof lastFocus.focus === "function") {
        lastFocus.focus();
      }
    };

    const open = (position) => {
      const entry = config.positions?.[String(position)];
      if (!entry) return;

      const isWin = entry.type === "win";
      modal.dataset.variant = isWin ? "win" : "lose";

      if (isWin) {
        const prize = entry.prize || "Prize";
        const line2 = String(config.winTitleLine2 || "The {prize} is yours.").replaceAll(
          "{prize}",
          prize
        );
        titleEl.innerHTML = `${config.winTitleLine1 || "You're in."}<br>${line2}`;
        subtitleEl.textContent = config.winSubtitle || "";
        if (heartEl) heartEl.hidden = true;
        if (imageEl) {
          imageEl.src = entry.image || imageEl.src;
          imageEl.alt = prize;
          imageEl.hidden = false;
          imageEl.style.display = "block";
        }
      } else {
        titleEl.textContent = config.loseTitle || "You were so close to winning!";
        subtitleEl.innerHTML = config.loseSubtitle || "";
        if (imageEl) {
          imageEl.hidden = true;
          imageEl.removeAttribute("alt");
          imageEl.style.display = "none";
        }
        if (heartEl) heartEl.hidden = false;
      }

      lastFocus = document.activeElement;
      modal.hidden = false;
      modal.setAttribute("aria-hidden", "false");
      document.body.classList.add("utmb26-lottery-modal-open");
      requestAnimationFrame(() => {
        modal.classList.add("is-open");
      });

      const closeBtn = modal.querySelector("[data-utmb26-lottery-modal-close]");
      closeBtn?.focus();
    };

    closeButtons.forEach((button) => {
      button.addEventListener("click", close);
    });

    modal.addEventListener("click", (event) => {
      if (event.target === modal) close();
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !modal.hidden) {
        close();
      }
    });

    return { open, close };
  };

  const setupNotice = (lottery, signal) => {
    const notice = lottery.querySelector("[data-utmb26-lottery-notice]");
    const textEl = lottery.querySelector("[data-utmb26-lottery-notice-text]");
    const closeButtons = lottery.querySelectorAll("[data-utmb26-lottery-notice-close]");

    if (!notice || !textEl) {
      return {
        open: () => {},
        close: () => {},
      };
    }

    let lastFocus = null;

    const close = () => {
      notice.classList.remove("is-open");
      notice.hidden = true;
      notice.setAttribute("aria-hidden", "true");
      document.body.classList.remove("utmb26-lottery-notice-open");
      if (lastFocus && typeof lastFocus.focus === "function") {
        lastFocus.focus();
      }
    };

    const open = (message) => {
      if (!message) return;
      textEl.textContent = message;
      lastFocus = document.activeElement;
      notice.hidden = false;
      notice.setAttribute("aria-hidden", "false");
      document.body.classList.add("utmb26-lottery-notice-open");
      requestAnimationFrame(() => {
        notice.classList.add("is-open");
      });
      closeButtons[0]?.focus();
    };

    closeButtons.forEach((button) => {
      button.addEventListener("click", close, { signal });
    });

    notice.addEventListener(
      "click",
      (event) => {
        if (event.target === notice) close();
      },
      { signal }
    );

    document.addEventListener(
      "keydown",
      (event) => {
        if (event.key === "Escape" && !notice.hidden) {
          close();
        }
      },
      { signal }
    );

    return { open, close };
  };

  const readEmailFromForm = (form) => {
    const input = form?.querySelector(
      'input[type="email"], input:not([type="checkbox"]):not([type="hidden"])'
    );
    return (input?.value || "").trim();
  };

  const setup = (lottery) => {
    if (lottery._utmb26Abort) {
      lottery._utmb26Abort.abort();
      instances.delete(lottery._utmb26Api);
    }

    const abort = new AbortController();
    const { signal } = abort;
    lottery._utmb26Abort = abort;

    const piece = lottery.querySelector("[data-utmb26-lottery-piece]");
    const button = lottery.querySelector("[data-utmb26-lottery-roll]");
    const status = lottery.querySelector("[data-utmb26-lottery-status]");
    const formWrap = lottery.querySelector("[data-utmb26-lottery-form]");
    const form = getKlaviyoFormRoot(lottery);
    const klaviyoFormId = getKlaviyoFormId(lottery, form);
    const postSubscribe = lottery.querySelector("[data-utmb26-lottery-post-subscribe]");
    const postWithCode = lottery.querySelector("[data-utmb26-lottery-post-with-code]");
    const postOffer = lottery.querySelector("[data-utmb26-lottery-post-offer]");
    const discountCodeEl = lottery.querySelector("[data-utmb26-lottery-discount-code]");
    const copyButton = lottery.querySelector("[data-utmb26-lottery-copy]");
    const copyBubble = lottery.querySelector("[data-utmb26-lottery-copy-bubble]");
    const diceSlot =
      lottery.querySelector("[data-utmb26-lottery-dice-slot]") ||
      button?.querySelector(".utmb26-lottery__dice") ||
      button;

    if (!piece || !button || !status) return;

    const modalApi = setupModal(lottery);
    const noticeApi = setupNotice(lottery, signal);

    let currentIndex = 0;
    let isMoving = false;
    let pendingSteps = null;
    let hasRolled = false;
    let cachedEmail = "";
    let isFetchingPrize = false;
    let hasHandledKlaviyoSuccess = false;
    let lastNoticeMessage = "";
    let currentDiscountCode = null;
    let copyBubbleTimer = 0;

    const thanksMessage =
      lottery.getAttribute("data-notice-thanks") || "Thanks for your registration!";

    const setStatus = (text) => {
      status.textContent = text;
      status.classList.toggle("is-visible", Boolean(text));
    };

    const applyPostSubscribeContent = (code) => {
      currentDiscountCode = code;
      const hasCode = Boolean(code);

      if (postSubscribe) {
        postSubscribe.classList.toggle("is-offer-fallback", !hasCode);
      }
      if (postWithCode) {
        postWithCode.hidden = !hasCode;
      }
      if (postOffer) {
        postOffer.hidden = hasCode;
      }
      if (hasCode && discountCodeEl) {
        discountCodeEl.textContent = code;
      }
    };

    const setDiscountCode = (payloadOrCode) => {
      const code =
        typeof payloadOrCode === "string" || payloadOrCode == null
          ? payloadOrCode
          : extractDiscountCode(payloadOrCode);
      applyPostSubscribeContent(code);
    };

    const showPostSubscribe = () => {
      if (formWrap) formWrap.classList.add("is-post-subscribe");
      if (form) {
        form.hidden = true;
        form.setAttribute("aria-hidden", "true");
      }
      if (postSubscribe) {
        postSubscribe.hidden = false;
        postSubscribe.removeAttribute("hidden");
      }
    };

    const hidePostSubscribe = () => {
      if (formWrap) formWrap.classList.remove("is-post-subscribe");
      if (form) {
        form.hidden = false;
        form.removeAttribute("aria-hidden");
      }
      if (postSubscribe) {
        postSubscribe.hidden = true;
      }
    };

    const openNotice = (message) => {
      lastNoticeMessage = message;
      noticeApi.open(message);
    };

    const cacheEmail = (email) => {
      const next = (email || readEmailFromForm(form) || "").trim();
      if (next) cachedEmail = next;
      return cachedEmail;
    };

    const hideCopyBubble = () => {
      if (copyBubbleTimer) {
        window.clearTimeout(copyBubbleTimer);
        copyBubbleTimer = 0;
      }
      if (copyBubble) copyBubble.hidden = true;
    };

    const showCopyBubble = () => {
      if (!copyBubble) return;
      copyBubble.hidden = false;
      if (copyBubbleTimer) window.clearTimeout(copyBubbleTimer);
      copyBubbleTimer = window.setTimeout(hideCopyBubble, COPY_BUBBLE_MS);
    };

    const copyDiscountCode = async () => {
      if (!currentDiscountCode) return;
      const code = currentDiscountCode;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(code);
        } else {
          const input = document.createElement("textarea");
          input.value = code;
          input.setAttribute("readonly", "");
          input.style.position = "absolute";
          input.style.left = "-9999px";
          document.body.appendChild(input);
          input.select();
          document.execCommand("copy");
          document.body.removeChild(input);
        }
        showCopyBubble();
      } catch (error) {
        console.error("UTMB26 lottery copy error:", error);
      }
    };

    if (copyButton) {
      copyButton.addEventListener("click", copyDiscountCode, { signal });
    }
    signal.addEventListener("abort", hideCopyBubble);

    const requestPrizeForEmail = async (emailFromEvent) => {
      if (isFetchingPrize || pendingSteps || hasRolled) return;

      const email = cacheEmail(emailFromEvent);
      if (!email) {
        setStatus(MSG_ENTER_EMAIL);
        return;
      }

      isFetchingPrize = true;
      let shouldAutoRoll = false;

      const keepKlaviyoThanks = (statusText) => {
        hidePostSubscribe();
        if (statusText) setStatus(statusText);
      };

      try {
        const data = await ajaxLottery(email);
        const payload = data?.data || {};
        const actStatus = payload.status ?? payload.act_status;

        // IP limit / server error: keep Klaviyo thank-you UI, do not switch to post-subscribe.
        if (actStatus === "no_finish_ip") {
          keepKlaviyoThanks(MSG_IP_LIMIT);
        } else if (actStatus === "ok") {
          const steps = getStepsForPriceName(payload.priceId);
          if (!steps) {
            keepKlaviyoThanks(MSG_SERVER_ERROR);
          } else {
            setDiscountCode(data);
            showPostSubscribe();
            pendingSteps = steps;
            setStatus(lottery.dataset.idleText || "Ready to roll");
            shouldAutoRoll = true;
          }
        } else {
          const errorMessage = getErrorMessage(actStatus);
          if (errorMessage === MSG_SERVER_ERROR) {
            keepKlaviyoThanks(MSG_SERVER_ERROR);
          } else {
            setDiscountCode(data);
            showPostSubscribe();
            openNotice(errorMessage);
            setStatus(errorMessage);
          }
        }
      } catch (error) {
        console.error("UTMB26 lottery error:", error);
        keepKlaviyoThanks(MSG_SERVER_ERROR);
      } finally {
        isFetchingPrize = false;
      }

      if (shouldAutoRoll) {
        await movePiece();
      }
    };

    const markKlaviyoSuccessUi = () => {
      if (hasHandledKlaviyoSuccess) return true;

      if (!form) {
        hasHandledKlaviyoSuccess = true;
        return true;
      }

      const thanks = form.querySelector("h1 span, h1");
      if (!thanks) return false;

      const text = thanks.textContent?.trim() || "";
      if (!/thanks for your registration/i.test(text)) {
        return false;
      }

      hasHandledKlaviyoSuccess = true;
      return true;
    };

    if (form) {
      form.addEventListener("input", () => cacheEmail(), { signal, capture: true });
      form.addEventListener("change", () => cacheEmail(), { signal, capture: true });
      form.addEventListener(
        "pointerdown",
        (event) => {
          if (event.target.closest("button")) cacheEmail();
        },
        { signal, capture: true }
      );

      if (!markKlaviyoSuccessUi()) {
        const observer = new MutationObserver(() => {
          if (markKlaviyoSuccessUi()) observer.disconnect();
        });
        observer.observe(form, { childList: true, subtree: true, characterData: true });
        signal.addEventListener("abort", () => observer.disconnect());
      }
    }

    const heading = lottery.querySelector(".utmb26-lottery__heading");

    const shakeHeading = () => {
      if (!heading || reduceMotion.matches) return;
      heading.classList.remove("is-shake");
      void heading.offsetWidth;
      heading.classList.add("is-shake");
    };

    if (heading) {
      heading.addEventListener(
        "animationend",
        () => {
          heading.classList.remove("is-shake");
        },
        { signal }
      );
    }

    const moveOneSpace = async () => {
      currentIndex = (currentIndex + 1) % track.length;
      const nextPosition = track[currentIndex];

      piece.classList.remove(...positionClasses, "is-stepping");
      lottery.classList.remove(...positionClasses, "is-stepping");
      piece.classList.add(`is-position-${nextPosition}`);
      lottery.classList.add(`is-position-${nextPosition}`);

      if (!reduceMotion.matches) {
        void piece.offsetWidth;
        piece.classList.add("is-stepping");
        await wait(430);
      }
    };

    const movePiece = async () => {
      if (isMoving) return;

      if (hasRolled || (!pendingSteps && lastNoticeMessage)) {
        if (lastNoticeMessage) noticeApi.open(lastNoticeMessage);
        return;
      }

      if (!pendingSteps) {
        setStatus(MSG_ENTER_EMAIL);
        shakeHeading();
        return;
      }

      const steps = pendingSteps;
      pendingSteps = null;
      isMoving = true;
      hasRolled = true;
      button.disabled = true;
      noticeApi.close();
      lottery.setAttribute("aria-busy", "true");

      showDiceForSteps(lottery, diceSlot, steps);
      setStatus(formatStatus(lottery.dataset.movingText, steps));

      if (!reduceMotion.matches) {
        await wait(DICE_ANIMATION_MS);
      }

      for (let step = 0; step < steps; step += 1) {
        await moveOneSpace();
      }

      piece.classList.remove("is-stepping");
      setStatus(formatStatus(lottery.dataset.resultText, steps));
      lottery.removeAttribute("aria-busy");
      isMoving = false;
      button.disabled = false;

      const finalPosition = track[currentIndex];
      if (finalPosition > 0) {
        modalApi.open(finalPosition);
      }
    };

    const api = {
      lottery,
      formId: klaviyoFormId,
      handleEmailSubmit: (email) => {
        hasHandledKlaviyoSuccess = true;
        return requestPrizeForEmail(email);
      },
    };

    lottery._utmb26Api = api;
    instances.add(api);

    button.addEventListener("click", movePiece, { signal });
    lottery.dataset.utmb26LotteryInitialized = "true";
  };

  const init = (root = document) => {
    if (root.matches?.("[data-utmb26-lottery]")) {
      setup(root);
    }

    root.querySelectorAll?.("[data-utmb26-lottery]").forEach(setup);
  };

  if (!window.__utmb26KlaviyoFormsBound) {
    window.__utmb26KlaviyoFormsBound = true;
    window.addEventListener("klaviyoForms", (event) => {
      const detail = event.detail || {};
      if (detail.type !== "submit") return;

      const email = detail.metaData?.$email || "";
      instances.forEach((api) => {
        if (!api?.lottery || !document.contains(api.lottery)) {
          instances.delete(api);
          return;
        }
        if (detail.formId && api.formId && detail.formId !== api.formId) return;
        api.handleEmailSubmit(email);
      });
    });
  }

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });

  window.utmb26Lottery = { init };
  init();
})();
